import { DayKey } from '../types';
import { fields } from './mockData';
import { getOwnerProfileById } from './authStore';
import {
  createSubscriptionActivatedNotifications,
  createSubscriptionApprovedNotification,
  createSubscriptionCancelledNotifications,
  createSubscriptionRequestNotification,
} from './engagementStore';

const SUBSCRIPTIONS_KEY = 'halisaha_subscriptions_v2';
const SUBSCRIBER_ID_KEY = 'halisaha_subscriber_id';
const OWNER_SUBSCRIPTION_BALANCES_KEY = 'halisaha_owner_subscription_balances_v1';

export interface FieldSubscription {
  id: string;
  fieldId: string;
  subscriberId: string;
  subscriberUserId?: string;
  subscriberPhone?: string;
  dayKey: DayKey;
  timeLabel: string; // HH:MM
  durationMinutes: 60;
  startDate: string; // YYYY-MM-DD
  status: 'pending_owner_approval' | 'awaiting_payment' | 'active' | 'cancelled';
  amountPaid: number;
  refundedAmount: number;
  approvedAt?: string;
  paidAt?: string;
  cancelledAt?: string;
  cancelledBy?: 'user' | 'owner';
  createdAt: string;
}

type OwnerSubscriptionBalances = Record<string, number>;

function readSubscriptions(): FieldSubscription[] {
  try {
    const raw = localStorage.getItem(SUBSCRIPTIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as FieldSubscription[];
  } catch {
    return [];
  }
}

function writeSubscriptions(list: FieldSubscription[]) {
  localStorage.setItem(SUBSCRIPTIONS_KEY, JSON.stringify(list));
}

function readOwnerBalances(): OwnerSubscriptionBalances {
  try {
    const raw = localStorage.getItem(OWNER_SUBSCRIPTION_BALANCES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as OwnerSubscriptionBalances;
  } catch {
    return {};
  }
}

function writeOwnerBalances(value: OwnerSubscriptionBalances) {
  localStorage.setItem(OWNER_SUBSCRIPTION_BALANCES_KEY, JSON.stringify(value));
}

function getOwnerIdByField(fieldId: string): string | undefined {
  return fields.find((item) => item.id === fieldId)?.ownerId;
}

function addOwnerBalance(ownerId: string | undefined, amount: number) {
  if (!ownerId || amount <= 0) return;
  const balances = readOwnerBalances();
  balances[ownerId] = (balances[ownerId] || 0) + amount;
  writeOwnerBalances(balances);
}

function subtractOwnerBalance(ownerId: string | undefined, amount: number) {
  if (!ownerId || amount <= 0) return;
  const balances = readOwnerBalances();
  const current = balances[ownerId] || 0;
  balances[ownerId] = Math.max(0, current - amount);
  writeOwnerBalances(balances);
}

function getDayKey(date: string): DayKey {
  const day = new Date(date).getDay();
  const keys: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return keys[day] || 'mon';
}

function dateToIso(value: Date): string {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isActiveForDate(item: FieldSubscription, date: string): boolean {
  return item.status === 'active' && date >= item.startDate;
}

export function getOrCreateSubscriberId(): string {
  const existing = localStorage.getItem(SUBSCRIBER_ID_KEY);
  if (existing) return existing;
  const created = `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem(SUBSCRIBER_ID_KEY, created);
  return created;
}

export function createMonthlySubscription(input: {
  fieldId: string;
  subscriberId: string;
  subscriberUserId?: string;
  subscriberPhone?: string;
  dayKey: DayKey;
  timeLabel: string;
  durationMinutes: 60;
  startDate?: string;
  amountPaid: number;
}): { ok: true; subscription: FieldSubscription } | { ok: false; error: string } {
  const today = dateToIso(new Date());
  const startDate = input.startDate || today;
  const list = readSubscriptions();

  const hasConflict = list.some((item) => {
    if (item.status !== 'active' && item.status !== 'awaiting_payment' && item.status !== 'pending_owner_approval') return false;
    if (item.fieldId !== input.fieldId) return false;
    if (item.dayKey !== input.dayKey) return false;
    if (item.timeLabel !== input.timeLabel) return false;
    return true;
  });

  if (hasConflict) {
    return { ok: false, error: 'Seçtiğiniz gün/saat başka bir kullanıcıya abonelikte ayrılmış.' };
  }

  const subscription: FieldSubscription = {
    id: `subscr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    fieldId: input.fieldId,
    subscriberId: input.subscriberId,
    subscriberUserId: input.subscriberUserId,
    subscriberPhone: input.subscriberPhone,
    dayKey: input.dayKey,
    timeLabel: input.timeLabel,
    durationMinutes: input.durationMinutes,
    startDate,
    status: 'pending_owner_approval',
    amountPaid: 0,
    refundedAmount: 0,
    createdAt: new Date().toISOString(),
  };

  writeSubscriptions([subscription, ...list]);
  const ownerId = getOwnerIdByField(input.fieldId);
  const owner = ownerId ? getOwnerProfileById(ownerId) : undefined;
  createSubscriptionRequestNotification({
    fieldId: input.fieldId,
    fieldName: fields.find((item) => item.id === input.fieldId)?.name || 'Saha',
    ownerId,
    ownerPhone: owner?.phone,
    dayLabel: input.dayKey.toUpperCase(),
    timeLabel: input.timeLabel,
  });
  return { ok: true, subscription };
}

export function approveSubscriptionByOwner(subscriptionId: string, fieldId: string): { ok: boolean; subscription?: FieldSubscription } {
  const list = readSubscriptions();
  const index = list.findIndex((item) => item.id === subscriptionId && item.fieldId === fieldId);
  if (index < 0) return { ok: false };
  const target = list[index];
  if (target.status !== 'pending_owner_approval') return { ok: false };
  const approved: FieldSubscription = {
    ...target,
    status: 'awaiting_payment',
    approvedAt: new Date().toISOString(),
  };
  list[index] = approved;
  writeSubscriptions(list);

  createSubscriptionApprovedNotification({
    fieldId: target.fieldId,
    fieldName: fields.find((item) => item.id === target.fieldId)?.name || 'Saha',
    customerId: target.subscriberUserId,
    customerPhone: target.subscriberPhone,
    dayLabel: target.dayKey.toUpperCase(),
    timeLabel: target.timeLabel,
  });

  return { ok: true, subscription: approved };
}

export function markSubscriptionPaid(subscriptionId: string, subscriberId: string, amountPaid: number): { ok: boolean; subscription?: FieldSubscription; error?: string } {
  if (amountPaid <= 0) return { ok: false, error: 'Ödeme tutarı geçersiz.' };
  const list = readSubscriptions();
  const index = list.findIndex((item) => item.id === subscriptionId);
  if (index < 0) return { ok: false, error: 'Abonelik bulunamadı.' };
  const target = list[index];
  if (target.subscriberId !== subscriberId) return { ok: false, error: 'Bu abonelik size ait değil.' };
  if (target.status !== 'awaiting_payment') return { ok: false, error: 'Abonelik ödeme bekleyen durumda değil.' };

  const active: FieldSubscription = {
    ...target,
    status: 'active',
    amountPaid,
    paidAt: new Date().toISOString(),
  };
  list[index] = active;
  writeSubscriptions(list);

  const ownerId = getOwnerIdByField(target.fieldId);
  addOwnerBalance(ownerId, amountPaid);
  const owner = ownerId ? getOwnerProfileById(ownerId) : undefined;

  createSubscriptionActivatedNotifications({
    fieldId: target.fieldId,
    fieldName: fields.find((item) => item.id === target.fieldId)?.name || 'Saha',
    customerId: target.subscriberUserId,
    customerPhone: target.subscriberPhone,
    ownerId,
    ownerPhone: owner?.phone,
    dayLabel: target.dayKey.toUpperCase(),
    timeLabel: target.timeLabel,
  });

  return { ok: true, subscription: active };
}

export function cancelSubscription(subscriptionId: string, subscriberId?: string): { ok: boolean; refundAmount: number } {
  const list = readSubscriptions();
  const index = list.findIndex((item) => item.id === subscriptionId);
  if (index < 0) return { ok: false, refundAmount: 0 };
  const target = list[index];
  if (subscriberId && target.subscriberId !== subscriberId) {
    return { ok: false, refundAmount: 0 };
  }
  if (target.status === 'cancelled') return { ok: false, refundAmount: 0 };

  const refundAmount = target.status === 'active' ? target.amountPaid : 0;
  list[index] = {
    ...target,
    status: 'cancelled',
    refundedAmount: refundAmount,
    cancelledAt: new Date().toISOString(),
    cancelledBy: 'user',
  };
  writeSubscriptions(list);
  const ownerId = getOwnerIdByField(target.fieldId);
  const owner = ownerId ? getOwnerProfileById(ownerId) : undefined;
  subtractOwnerBalance(ownerId, refundAmount);
  createSubscriptionCancelledNotifications({
    fieldId: target.fieldId,
    fieldName: fields.find((item) => item.id === target.fieldId)?.name || 'Saha',
    customerId: target.subscriberUserId,
    customerPhone: target.subscriberPhone,
    ownerId,
    ownerPhone: owner?.phone,
    cancelledBy: 'user',
    refundAmount,
  });
  return { ok: true, refundAmount };
}

export function cancelSubscriptionByOwner(subscriptionId: string, fieldId: string): { ok: boolean; refundAmount: number } {
  const list = readSubscriptions();
  const index = list.findIndex((item) => item.id === subscriptionId && item.fieldId === fieldId);
  if (index < 0) return { ok: false, refundAmount: 0 };
  const target = list[index];
  if (target.status === 'cancelled') return { ok: false, refundAmount: 0 };

  const refundAmount = target.status === 'active' ? target.amountPaid : 0;
  list[index] = {
    ...target,
    status: 'cancelled',
    refundedAmount: refundAmount,
    cancelledAt: new Date().toISOString(),
    cancelledBy: 'owner',
  };
  writeSubscriptions(list);
  const ownerId = getOwnerIdByField(target.fieldId);
  const owner = ownerId ? getOwnerProfileById(ownerId) : undefined;
  subtractOwnerBalance(ownerId, refundAmount);
  createSubscriptionCancelledNotifications({
    fieldId: target.fieldId,
    fieldName: fields.find((item) => item.id === target.fieldId)?.name || 'Saha',
    customerId: target.subscriberUserId,
    customerPhone: target.subscriberPhone,
    ownerId,
    ownerPhone: owner?.phone,
    cancelledBy: 'owner',
    refundAmount,
  });
  return { ok: true, refundAmount };
}

export function getSubscriberSubscriptions(fieldId: string, subscriberId: string): FieldSubscription[] {
  return readSubscriptions()
    .filter((item) => item.fieldId === fieldId && item.subscriberId === subscriberId && item.status === 'active')
    .sort((a, b) => a.timeLabel.localeCompare(b.timeLabel));
}

export function getSubscriberSubscriptionsAll(fieldId: string, subscriberId: string): FieldSubscription[] {
  return readSubscriptions()
    .filter((item) => item.fieldId === fieldId && item.subscriberId === subscriberId && item.status !== 'cancelled')
    .sort((a, b) => a.timeLabel.localeCompare(b.timeLabel));
}

export function getFieldSubscriptions(fieldId: string): FieldSubscription[] {
  return readSubscriptions()
    .filter((item) => item.fieldId === fieldId && item.status === 'active')
    .sort((a, b) => a.timeLabel.localeCompare(b.timeLabel));
}

export function getFieldSubscriptionsForOwner(fieldId: string): FieldSubscription[] {
  return readSubscriptions()
    .filter((item) => item.fieldId === fieldId && item.status !== 'cancelled')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function isSlotOwnedBySubscriber(fieldId: string, date: string, timeLabel: string, subscriberId: string): boolean {
  const dayKey = getDayKey(date);
  return readSubscriptions().some(
    (item) =>
      item.fieldId === fieldId &&
      item.subscriberId === subscriberId &&
      item.dayKey === dayKey &&
      item.timeLabel === timeLabel &&
      isActiveForDate(item, date)
  );
}

export function isSlotBlockedBySubscription(fieldId: string, date: string, timeLabel: string, subscriberId?: string): boolean {
  const dayKey = getDayKey(date);
  const owner = readSubscriptions().find(
    (item) =>
      item.fieldId === fieldId &&
      item.dayKey === dayKey &&
      item.timeLabel === timeLabel &&
      isActiveForDate(item, date)
  );
  if (!owner) return false;
  if (!subscriberId) return true;
  return owner.subscriberId !== subscriberId;
}

export function getSubscriberCount(fieldId: string): number {
  const set = new Set(
    readSubscriptions()
      .filter((item) => item.fieldId === fieldId && item.status === 'active')
      .map((item) => item.subscriberId)
  );
  return set.size;
}

export function getOwnerSubscriptionBalance(ownerId: string): number {
  const balances = readOwnerBalances();
  return balances[ownerId] || 0;
}
